import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import axios from 'axios'
import { FilePlus, FolderPlus, Upload } from 'lucide-react'
import { type ChangeEvent, type ReactNode, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { useExpandedFolders } from '@/hooks/useExpandedFolders'
import {
  type KnowledgeDocumentListItem,
  useKnowledgeDocuments,
  useUpdateKnowledgeDocument,
  useUploadKnowledgeDocument,
} from '@/models/knowledgeDocument'
import {
  ancestorIds,
  collectSubtreeIds,
  groupFoldersByParent,
  type KnowledgeFolderItem,
  useDeleteKnowledgeFolder,
  useKnowledgeFolders,
  useUpdateKnowledgeFolder,
} from '@/models/knowledgeFolder'
import { DOCUMENT_DRAG_PREFIX, DocumentRow, DocumentRowContent } from './DocumentRow'
import { FolderDialog, type FolderDialogTarget } from './FolderDialog'
import { FolderDropZone } from './FolderDropZone'
import { FOLDER_DRAG_PREFIX, FolderRow, FolderRowContent } from './FolderRow'
import { NewDocumentDialog } from './NewDocumentDialog'
import { RootDropZone } from './RootDropZone'

const UPLOAD_ACCEPT = '.pdf,.xlsx,.xls,.csv,.tsv,.txt,.docx,.doc,.png,.jpg,.jpeg,.gif,.webp'

type DragTarget =
  { type: 'document'; doc: KnowledgeDocumentListItem } | { type: 'folder'; folder: KnowledgeFolderItem }

type Props = {
  selectedSlug: string | null
  onSelect: (slug: string) => void
}

export function KnowledgeTree({ selectedSlug, onSelect }: Props) {
  const { data: docs, isLoading: docsLoading } = useKnowledgeDocuments()
  const { data: folders, isLoading: foldersLoading } = useKnowledgeFolders()

  const [newDocFolder, setNewDocFolder] = useState<{ id: string | null } | null>(null)
  const [folderDialog, setFolderDialog] = useState<FolderDialogTarget | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeFolderItem | null>(null)
  const [dragging, setDragging] = useState<DragTarget | null>(null)

  const uploadMutation = useUploadKnowledgeDocument()
  const updateDocMutation = useUpdateKnowledgeDocument()
  const updateFolderMutation = useUpdateKnowledgeFolder()
  const deleteFolderMutation = useDeleteKnowledgeFolder()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const folderList = useMemo(() => folders ?? [], [folders])
  const selectedFolderId = docs?.find((d) => d.slug === selectedSlug)?.folder ?? null
  const revealChain = useMemo(
    () => (selectedFolderId ? [selectedFolderId, ...ancestorIds(folderList, selectedFolderId)] : []),
    [folderList, selectedFolderId]
  )
  const { expanded, toggle, expandAll } = useExpandedFolders('knowledge-base-expanded-folders', revealChain)

  const foldersByParent = useMemo(() => groupFoldersByParent(folderList), [folderList])
  const docsByFolder = useMemo(() => {
    const map = new Map<string | null, KnowledgeDocumentListItem[]>()
    for (const doc of docs ?? []) {
      const siblings = map.get(doc.folder) ?? []
      siblings.push(doc)
      map.set(doc.folder, siblings)
    }
    for (const siblings of map.values()) {
      siblings.sort((a, b) => a.title.localeCompare(b.title))
    }
    return map
  }, [docs])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const isLoading = docsLoading || foldersLoading

  const handleFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // let the same file be re-selected later
    if (!file) return
    try {
      const doc = await uploadMutation.mutateAsync({ file, folder: null })
      onSelect(doc.slug)
      toast.success(`Created “${doc.title}” from ${file.name}`)
    } catch (err) {
      const data = axios.isAxiosError(err)
        ? (err.response?.data as { error?: string } | undefined)
        : undefined
      toast.error(data?.error || 'Could not extract text from this file.')
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    setDragging(null)
    const { active, over } = event
    if (!over) return

    const overId = String(over.id)
    const targetFolderId = overId.startsWith(FOLDER_DRAG_PREFIX)
      ? overId.slice(FOLDER_DRAG_PREFIX.length)
      : null
    const activeId = String(active.id)

    if (activeId.startsWith(DOCUMENT_DRAG_PREFIX)) {
      const slug = activeId.slice(DOCUMENT_DRAG_PREFIX.length)
      const doc = docs?.find((d) => d.slug === slug)
      if (!doc || doc.folder === targetFolderId) return
      if (targetFolderId) expandAll([targetFolderId])
      updateDocMutation.mutate(
        { slug, folder: targetFolderId },
        { onError: () => toast.error('Could not move that document.') }
      )
      return
    }

    if (!activeId.startsWith(FOLDER_DRAG_PREFIX)) return
    const folderId = activeId.slice(FOLDER_DRAG_PREFIX.length)
    const folder = folderList.find((f) => f.id === folderId)
    if (!folder || folder.parent === targetFolderId || folderId === targetFolderId) return
    // The server rejects this too; bailing avoids a 400 on a common mis-drag.
    if (targetFolderId && collectSubtreeIds(folderList, folderId).has(targetFolderId)) {
      toast.error('A folder cannot be moved inside itself.')
      return
    }
    if (targetFolderId) expandAll([targetFolderId])
    updateFolderMutation.mutate(
      { id: folderId, parent: targetFolderId },
      { onError: () => toast.error('Could not move that folder.') }
    )
  }

  function renderLevel(parent: string | null, depth: number): ReactNode {
    const childFolders = foldersByParent.get(parent) ?? []
    const childDocs = docsByFolder.get(parent) ?? []
    return (
      <>
        {childFolders.map((folder) => {
          const isExpanded = expanded.has(folder.id)
          const childCount =
            (foldersByParent.get(folder.id)?.length ?? 0) + (docsByFolder.get(folder.id)?.length ?? 0)
          return (
            <FolderDropZone key={folder.id} folder={folder}>
              {(isOver) => (
                <>
                  <FolderRow
                    folder={folder}
                    depth={depth}
                    expanded={isExpanded}
                    childCount={childCount}
                    isOver={isOver}
                    onToggle={() => toggle(folder.id)}
                    onNewDocument={() => setNewDocFolder({ id: folder.id })}
                    onNewSubfolder={() =>
                      setFolderDialog({ mode: 'create', parent: folder.id, parentPath: folder.path })
                    }
                    onEdit={() => setFolderDialog({ mode: 'edit', folder })}
                    onDelete={() => setDeleteTarget(folder)}
                  />
                  {isExpanded && renderLevel(folder.id, depth + 1)}
                </>
              )}
            </FolderDropZone>
          )
        })}
        {childDocs.map((doc) => (
          <DocumentRow
            key={doc.id}
            doc={doc}
            depth={depth}
            selected={doc.slug === selectedSlug}
            onSelect={() => onSelect(doc.slug)}
          />
        ))}
      </>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button className="flex-1" onClick={() => setNewDocFolder({ id: null })}>
          <FilePlus /> New document
        </Button>
        <Button
          variant="outline"
          size="icon"
          title="New folder"
          aria-label="New folder"
          onClick={() => setFolderDialog({ mode: 'create', parent: null, parentPath: '' })}
        >
          <FolderPlus />
        </Button>
        <Button
          variant="outline"
          size="icon"
          title="Upload a document — creates a KB entry from its extracted text"
          aria-label="Upload a document"
          loading={uploadMutation.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={UPLOAD_ACCEPT}
          onChange={handleFileSelected}
        />
      </div>

      <NewDocumentDialog
        open={newDocFolder !== null}
        folder={newDocFolder?.id ?? null}
        onOpenChange={(open) => !open && setNewDocFolder(null)}
        onCreated={(slug) => onSelect(slug)}
      />
      <FolderDialog
        target={folderDialog}
        onOpenChange={(open) => !open && setFolderDialog(null)}
        onSaved={(folder) =>
          expandAll(folder.parent ? [folder.parent, ...ancestorIds(folderList, folder.parent)] : [])
        }
      />
      <ConfirmationDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete “${deleteTarget?.name ?? ''}”?`}
        description="Its documents and subfolders move up one level — nothing is deleted with it."
        confirmLabel="Delete folder"
        onConfirm={async () => {
          if (!deleteTarget) return
          try {
            await deleteFolderMutation.mutateAsync(deleteTarget.id)
            setDeleteTarget(null)
          } catch (err) {
            // A 409 names the folder to rename; without this the dialog would
            // just sit there having quietly failed.
            const detail = axios.isAxiosError(err)
              ? (err.response?.data as { detail?: string } | undefined)?.detail
              : undefined
            toast.error(detail || 'Could not delete that folder.')
            setDeleteTarget(null)
          }
        }}
      />

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
          <Skeleton className="h-11 w-full" />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          // The root wraps every folder, so area-based detection always picks it.
          collisionDetection={pointerWithin}
          onDragStart={(event: DragStartEvent) =>
            setDragging((event.active.data.current as DragTarget | undefined) ?? null)
          }
          onDragCancel={() => setDragging(null)}
          onDragEnd={handleDragEnd}
        >
          <RootDropZone>
            {folderList.length || docs?.length ? (
              renderLevel(null, 0)
            ) : (
              <p className="text-muted-foreground text-sm">No documents yet. Create one to get started.</p>
            )}
          </RootDropZone>
          <DragOverlay dropAnimation={null}>
            {dragging ? (
              <div className="w-72 rotate-1 opacity-90">
                {dragging.type === 'folder' ? (
                  <FolderRowContent folder={dragging.folder} />
                ) : (
                  <DocumentRowContent doc={dragging.doc} />
                )}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  )
}
